/**
 * Guided BigQuery setup: generates the copy-paste script that provisions a
 * `qombra-connector` service account in the customer's GCP project, grants the
 * roles Qombra needs (read everything, run jobs, Storage API reads, write its
 * own qombra_-prefixed tables), mints a JSON key onto the user's Desktop and —
 * when the org blocks key creation — applies a temporary project-scoped
 * org-policy override that is always restored afterwards.
 *
 * The project id is validated before interpolation so nothing shell-hostile
 * can land in the copied script.
 */

// GCP project ids: lowercase letters, digits and hyphens; legacy domain-scoped
// ids also carry dots and a colon (mirrors _PROJECT_RE in connectors/bigquery.py).
const PROJECT_ID_RE = /^[a-z0-9][a-z0-9.-]*(:[a-z0-9][a-z0-9.-]*)?$/i;

export const isValidProjectId = (projectId) =>
  PROJECT_ID_RE.test(String(projectId || '').trim());

export const detectOS = () => {
  const probe = `${navigator.userAgent || ''} ${navigator.platform || ''}`;
  return /win/i.test(probe) ? 'windows' : 'unix';
};

export const GCLOUD_INSTALL_COMMANDS = {
  mac: 'brew install --cask gcloud-cli',
  linux: 'curl https://sdk.cloud.google.com | bash',
  windows: 'winget install Google.CloudSDK',
};

export const installCommandFor = (os) => {
  if (os === 'windows') return GCLOUD_INSTALL_COMMANDS.windows;
  return /mac/i.test(navigator.userAgent || '')
    ? GCLOUD_INSTALL_COMMANDS.mac
    : GCLOUD_INSTALL_COMMANDS.linux;
};

const SA_NAME = 'qombra-connector';
const KEY_FILENAME = 'qombra-bigquery-key.json';
const ROLES = ['roles/bigquery.jobUser', 'roles/bigquery.readSessionUser', 'roles/bigquery.dataEditor'];

// No shebang, and the body runs via \`bash <<'...'\`: this is pasted into an
// interactive terminal, where a shebang trips zsh history expansion
// ("event not found") and where set -e / functions / the EXIT trap would
// otherwise leak into (or terminate) the user's own shell.
const bashScript = (projectId) => `bash <<'QOMBRA_SETUP'
set -euo pipefail
PROJECT="${projectId}"
SA="${SA_NAME}"; SA_EMAIL="$SA@$PROJECT.iam.gserviceaccount.com"
DEST="$HOME/Desktop"; [ -d "$DEST" ] || DEST="$HOME"
KEY_PATH="$DEST/${KEY_FILENAME}"

# 1. Service account (skipped if it already exists)
gcloud iam service-accounts describe "$SA_EMAIL" --project "$PROJECT" >/dev/null 2>&1 \\
  || gcloud iam service-accounts create "$SA" --project "$PROJECT" --display-name "Qombra BigQuery Connector"

# 2. Roles: run queries, Storage API reads, read tables + write Qombra's own qombra_ tables
#    (retried: IAM needs a moment to propagate a fresh service account)
for ROLE in ${ROLES.join(' ')}; do
  for i in 1 2 3 4 5; do
    gcloud projects add-iam-policy-binding "$PROJECT" --member "serviceAccount:$SA_EMAIL" \\
      --role "$ROLE" --condition=None --quiet >/dev/null 2>&1 && break
    sleep 6
  done
done

# 3. Key file. If your organization blocks service-account keys, temporarily
#    lift that policy for THIS project only, then restore it exactly as it was.
create_key() { gcloud iam service-accounts keys create "$KEY_PATH" --iam-account "$SA_EMAIL" >/dev/null 2>&1; }
if ! create_key; then
  echo "Key creation is blocked by org policy - applying a temporary project-scoped override..."
  HAD_POLICY=$(gcloud org-policies describe iam.disableServiceAccountKeyCreation --project "$PROJECT" >/dev/null 2>&1 && echo yes || echo no)
  restore_policy() {
    if [ "$HAD_POLICY" = no ]; then
      gcloud resource-manager org-policies delete iam.disableServiceAccountKeyCreation --project "$PROJECT" >/dev/null 2>&1
    else
      gcloud resource-manager org-policies enable-enforce iam.disableServiceAccountKeyCreation --project "$PROJECT" >/dev/null 2>&1
    fi
    echo "Org policy restored."
  }
  trap restore_policy EXIT
  gcloud resource-manager org-policies disable-enforce iam.disableServiceAccountKeyCreation --project "$PROJECT" >/dev/null
  for i in $(seq 1 18); do create_key && break; sleep 10; done  # policy propagation takes ~1-2 min
fi
[ -s "$KEY_PATH" ] || { echo "ERROR: the key could not be created."; exit 1; }
chmod 600 "$KEY_PATH"
echo ""
echo "Done - key saved to: $KEY_PATH"
echo "Upload this file in Qombra to finish connecting."
QOMBRA_SETUP
`;

const powershellScript = (projectId) => `$ErrorActionPreference = "Continue"
$PROJECT = "${projectId}"
$SA = "${SA_NAME}"; $SA_EMAIL = "$SA@$PROJECT.iam.gserviceaccount.com"
$DEST = Join-Path $HOME "Desktop"; if (-not (Test-Path $DEST)) { $DEST = $HOME }
$KEY_PATH = Join-Path $DEST "${KEY_FILENAME}"

# 1. Service account (skipped if it already exists)
gcloud iam service-accounts describe $SA_EMAIL --project $PROJECT *> $null
if ($LASTEXITCODE -ne 0) {
  gcloud iam service-accounts create $SA --project $PROJECT --display-name "Qombra BigQuery Connector"
}

# 2. Roles: run queries, Storage API reads, read tables + write Qombra's own qombra_ tables
#    (retried: IAM needs a moment to propagate a fresh service account)
foreach ($ROLE in @(${ROLES.map((r) => `"${r}"`).join(', ')})) {
  for ($i = 0; $i -lt 5; $i++) {
    gcloud projects add-iam-policy-binding $PROJECT --member "serviceAccount:$SA_EMAIL" \`
      --role $ROLE --condition=None --quiet *> $null
    if ($LASTEXITCODE -eq 0) { break }
    Start-Sleep 6
  }
}

# 3. Key file. If your organization blocks service-account keys, temporarily
#    lift that policy for THIS project only, then restore it exactly as it was.
function Create-Key { gcloud iam service-accounts keys create $KEY_PATH --iam-account $SA_EMAIL *> $null; return ($LASTEXITCODE -eq 0) }
if (-not (Create-Key)) {
  Write-Host "Key creation is blocked by org policy - applying a temporary project-scoped override..."
  gcloud org-policies describe iam.disableServiceAccountKeyCreation --project $PROJECT *> $null
  $HAD_POLICY = ($LASTEXITCODE -eq 0)
  try {
    gcloud resource-manager org-policies disable-enforce iam.disableServiceAccountKeyCreation --project $PROJECT *> $null
    for ($i = 0; $i -lt 18; $i++) {  # policy propagation takes ~1-2 min
      if (Create-Key) { break }
      Start-Sleep 10
    }
  } finally {
    if ($HAD_POLICY) {
      gcloud resource-manager org-policies enable-enforce iam.disableServiceAccountKeyCreation --project $PROJECT *> $null
    } else {
      gcloud resource-manager org-policies delete iam.disableServiceAccountKeyCreation --project $PROJECT *> $null
    }
    Write-Host "Org policy restored."
  }
}
if (-not (Test-Path $KEY_PATH)) { Write-Host "ERROR: the key could not be created."; exit 1 }
Write-Host ""
Write-Host "Done - key saved to: $KEY_PATH"
Write-Host "Upload this file in Qombra to finish connecting."
`;

export const buildGuidedScript = (projectId, os) => {
  const id = String(projectId || '').trim();
  if (!isValidProjectId(id)) return null;
  return os === 'windows' ? powershellScript(id) : bashScript(id);
};

export { KEY_FILENAME };
